/**
 * Block registry — the single manifest mapping every marketing block `type` to
 * its primitive. `MarketingPage`'s dispatcher is GENERIC over this registry
 * (it never hand-switches on type), mirroring how `PAGE_REGISTRY` (site.ts)
 * drives routes. Adding a block type = ONE entry here + its `@ix:` marker in a
 * `/docs/marketing` doc — no renderer edit.
 *
 *   - `wide`      → the block runs the full band width (not the reading measure)
 *   - `recursive` → the block has a `body: MarketingBlock[]` the dispatcher
 *                   renders into it as children (e.g. prose-section)
 *
 * Special rendering (the hero's right-column demo, demo-outtake's FlowDemo) lives
 * INSIDE the primitives (they take a `demoId` and render the FlowDemo themselves),
 * so every block dispatches uniformly as `<Component {...props} />`.
 */
import type { ComponentType } from 'react';
import {
  Hero, SectionHeader, Prose, PullQuote, Figure, FigureGrid, Split, Callout,
  StatCard, StatGrid, DemoOuttake, PdfDownload, ComparisonTable, FeatureGrid,
  Cta, Faq, ProseSection,
  ProviderDirectory, UniverseMap, InferField, Divider, ContactForm, ApiReference,
} from './blocks/index';
import type {
  HeroProps, SectionHeaderProps, ProseProps, PullQuoteProps, FigureProps,
  FigureGridProps, SplitProps, CalloutProps, StatCardProps, StatGridProps,
  DemoOuttakeProps, PdfDownloadProps, ComparisonTableProps, FeatureGridProps,
  CtaProps, FaqProps, ProseSectionProps, ProviderDirectoryProps, UniverseMapProps,
  InferFieldProps, DividerProps, ContactFormProps, ApiReferenceProps,
} from './blocks/index';

/** A marketing content block: a `type` discriminant + that block's payload.
 *  Any block may also carry `wide: true` to render at the full content width
 *  (the page) instead of its default reading-measure lane — see `laned()`. */
export type MarketingBlock = (
  | ({ type: 'hero' } & HeroProps)
  | ({ type: 'section-header'; bg?: 'accent' } & SectionHeaderProps)
  | ({ type: 'prose' } & ProseProps)
  | ({ type: 'pull-quote' } & PullQuoteProps)
  | ({ type: 'figure' } & FigureProps)
  | ({ type: 'figure-grid' } & FigureGridProps)
  | ({ type: 'split' } & SplitProps)
  | ({ type: 'callout' } & CalloutProps)
  | ({ type: 'stat-card' } & StatCardProps)
  | ({ type: 'stat-grid' } & StatGridProps)
  | ({ type: 'demo-outtake' } & DemoOuttakeProps)
  | ({ type: 'pdf-download' } & PdfDownloadProps)
  | ({ type: 'comparison-table' } & ComparisonTableProps)
  | ({ type: 'feature-grid' } & FeatureGridProps)
  | ({ type: 'cta' } & CtaProps)
  | ({ type: 'faq' } & FaqProps)
  | ({ type: 'prose-section'; body: readonly MarketingBlock[] } & Omit<ProseSectionProps, 'children'>)
  | ({ type: 'provider-directory' } & ProviderDirectoryProps)
  | ({ type: 'universe-map' } & UniverseMapProps)
  | ({ type: 'infer-field' } & InferFieldProps)
  | ({ type: 'contact-form' } & ContactFormProps)
  | ({ type: 'api-reference' } & ApiReferenceProps)
  | ({ type: 'divider' } & DividerProps)
) & { readonly wide?: boolean };

interface BlockDef {
  readonly component: ComponentType<Record<string, unknown>>;
  readonly wide?: boolean;
  readonly recursive?: boolean;
}

/**
 * Register a primitive. Block props are validated at the manifest layer (the
 * generated *-blocks.ts conform to `MarketingBlock`), so the dispatcher is
 * intentionally prop-erased — the one type-erasing cast lives here, not at 24
 * call sites and not as `any`.
 */
function reg<P extends object>(component: ComponentType<P>, opts: Omit<BlockDef, 'component'> = {}): BlockDef {
  return { component: component as unknown as ComponentType<Record<string, unknown>>, ...opts };
}

export const BLOCK_REGISTRY: Record<MarketingBlock['type'], BlockDef> = {
  'hero': reg(Hero),
  'section-header': reg(SectionHeader),
  'prose': reg(Prose),
  'pull-quote': reg(PullQuote),
  'figure': reg(Figure),
  'figure-grid': reg(FigureGrid, { wide: true }),
  'split': reg(Split, { wide: true }),
  'callout': reg(Callout),
  'stat-card': reg(StatCard),
  'stat-grid': reg(StatGrid, { wide: true }),
  'demo-outtake': reg(DemoOuttake),
  'pdf-download': reg(PdfDownload),
  'comparison-table': reg(ComparisonTable, { wide: true }),
  'feature-grid': reg(FeatureGrid, { wide: true }),
  'cta': reg(Cta, { wide: true }),
  'faq': reg(Faq),
  'prose-section': reg(ProseSection, { recursive: true }),
  'provider-directory': reg(ProviderDirectory, { wide: true }),
  'universe-map': reg(UniverseMap, { wide: true }),
  'infer-field': reg(InferField),
  'contact-form': reg(ContactForm),
  'api-reference': reg(ApiReference, { wide: true }),
  'divider': reg(Divider),
};

/** Block types that run the full band width (derived from the registry). */
export const WIDE_BLOCKS: ReadonlySet<MarketingBlock['type']> = new Set(
  (Object.entries(BLOCK_REGISTRY) as [MarketingBlock['type'], BlockDef][])
    .filter(([, def]) => def.wide)
    .map(([type]) => type),
);
